import {Button, Icon, Loading, useLocation, useNavigate} from '@startlibs/components';
import {getColor, getFetcher} from '@startlibs/utils';
import {useToggle} from '@startlibs/core'
import React, {useEffect, useMemo, useRef} from 'react'
import {jwtDecode} from 'jwt-decode'
import styled from 'styled-components'
import {Card, CardHeader, SignInLayout, SignInMessageBox} from '../components/SigninLayout';
import {JwtAuthenticationWithLocale} from './JwtAuthentication'
import {TwoFactorAuthentication} from './TwoFactorAuthentication'
import {FormattedMessage, useIntl} from "react-intl";

// max-width: ${props => props.accountSettings ? `39rem` : `35rem`};
const SignInCard = styled(Card)`
  max-width: ${props => props.accountSettings ? `42rem` : `40rem`};
  margin: auto;
  transition: 0.25s ease;
  text-align: center;
  h2 {
    font-size: 20px;
    margin-bottom: 1.5rem;
    color: ${getColor('main')};
    &.warning {
      margin-bottom: 0.5rem;
      color: ${getColor('alert')};
    }
  }
  p {
    max-width: 25rem;
    margin: 0 auto 2rem;
  }
  ${Icon} {
    font-size: 70px;
    color: ${getColor('alert')};
  }
`

const isSafeRedirectUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.href)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

const INVALID_CREDENTIALS = (intl) => intl.formatMessage({
  defaultMessage: 'Invalid credentials',
  description: 'Login error message when credentials are invalid'
})

const refreshTimer = (time, system) => {

  if (time > 1) {
    return time - 1
  }

  // Redirecting...
  if(system.ssoEntities.length === 1){
    if((system.ssoEntities[0].url.length > 0) && (time === 1) && isSafeRedirectUrl(system.ssoEntities[0].url)){
      window.location.href = system.ssoEntities[0].url
    }
  }
  
  return 0;
}

export const RedirectLoading = styled.div`
  position: relative;
  margin: 2rem auto;
  font-size: 20px;
  font-weight: bold;
  border-radius: 50%;
  width: 52px;
  height: 52px;
  line-height: 52px;
  text-align: center;
`

export const SsoLogin = ({system, fromActivation, needsRegistration, userToggle, loginToSettings}) => {
  const intl = useIntl()
  const loading = useToggle()
  const accountSettings = useToggle()
  const expiredPassword = useToggle()
  const verify2fa = useToggle()
  const timeToRedirect = useToggle()
  const loginFailed = useToggle()
  const logout = useToggle()
  const navigate = useNavigate()
  const location = useLocation()
  const formRef = useRef()

  const searchParams = useMemo(() => new URLSearchParams(window.location.search),[])

  const jwt = useMemo(() => {
    return searchParams.has("jwt") ? jwtDecode(searchParams.get("jwt")) : {}
  },[])

  const passwordExpired = (key) => {
    verify2fa.close()
    expiredPassword.openWith(key)
  }

  const onSuccess = (_, {idToken,id_token,username,preAuthToken}) => {
    if (username) {
      needsRegistration(idToken,username)
    } else if (preAuthToken) {
      verify2fa.openWith(preAuthToken)
    } else {
      concludeLogin(id_token)
    }
  }

  const concludeLogin = (id_token) => {
    if (accountSettings.isOpen) {
      loading.open()
      getFetcher('/pasapi/account/info', null, {headers: {'Authorization': `Bearer ${id_token}`}})
        .then((result) => {
          userToggle.openWith({...result, id_token})
          navigate('/account?systemId='+system.systemId)
        })
    } else {
      loading.open()
      const authRedirectUrl = system.systemWebHooks.systemAuthWebHook + id_token + "&locale="+intl.locale
      if (isSafeRedirectUrl(authRedirectUrl)) {
        window.location = authRedirectUrl
      }
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    // if(this.props.system.systemWebHooks && this.props.system.systemWebHooks.migratedUrl){
    //   window.location.href = this.props.system.systemWebHooks.migratedUrl+this.props.location.pathname+this.props.location.search;
    // }
    if (urlParams.get("loginFailure")==='true') {
      loginFailed.open()
    }else{
      if (urlParams.get("logout")==='true') {
        logout.open()
      }else{
        timeToRedirect.openWith(5)
        setInterval(() => {
          timeToRedirect.openWith((prev) => refreshTimer(prev, system));
        }, 1000);
      }
    }
    if (urlParams.get("preAuthToken")) {
      verify2fa.openWith(urlParams.get("preAuthToken"))
    }
    if (loginToSettings) {
      accountSettings.open()
    }
  }, []);

  const values = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return {
      ...(urlParams.get("username") ? {username: urlParams.get("username")} : {}),
      systemId: system.systemId
    }
  },[])


  const expiredSession = location.state && location.state.expiredSession

  if (verify2fa.isOpen) {
    return <TwoFactorAuthentication
      system={system}
      userToggle={userToggle}
      preAuthToken={verify2fa.isOpen}
      concludeLogin={concludeLogin}
      passwordExpired={passwordExpired}
      username={values.username}
    />
  }

  if (jwt.sub && !expiredPassword.isOpen) {
    return <JwtAuthenticationWithLocale
      passwordExpired={passwordExpired}
      needsRegistration={needsRegistration}
      system={system}
      verify2fa={verify2fa}
      parsedJwt={jwt}
      jwt={searchParams.get("jwt")}
      userToggle={userToggle}
    />
  }

  return (
    <SignInLayout hideSupportMessage system={system}>
      {
        fromActivation &&
        <SignInMessageBox><FormattedMessage
          defaultMessage="Email address successfully updated."
          description="Email address successfully updated message"
        />
        </SignInMessageBox>
      }
      {
        expiredSession &&
        <SignInMessageBox warning><FormattedMessage
          defaultMessage="Your session has expired. Please sign in again."
          description="Session expired message"
        />
        </SignInMessageBox>
      }

      {loginFailed.isOpen && <SignInCard accountSettings={accountSettings.isOpen}>
        {/* Only one sso provider */}
        <Icon icon="warning"/>
        <h2 className="warning"><FormattedMessage
          defaultMessage='This account has been suspended'
          description='Login error message when account is suspended'
        /></h2>
        <p><FormattedMessage
          defaultMessage='Please contact your administrator'
          description='Login error message when account is suspended, instructions'
        /></p>
        {system.ssoEntities.length === 1 &&
          <>
            {system.ssoEntities.map((sso, index) => <Button.a key={index} highlight href={sso.url} css="min-width:12rem;"><FormattedMessage
              defaultMessage="Try again"
              description="Login error message, try again button"
            /></Button.a>)}
          </>
        }

        {/* More than one sso provider */}
        {system.ssoEntities.length > 1 && <>
          <CardHeader>
            <p><FormattedMessage
              defaultMessage="Maybe you can try using another Single Sign On provider"
              description="Login error message, try another SSO provider"
            /></p>
          </CardHeader>
          {system.ssoEntities.map((sso, index) =>
            <div css="margin: 12px;" key={index}>
              <Button.a highlight href={sso.url}>{sso.label || intl.formatMessage({
                defaultMessage:'Use Single Sign On',
                description:'Login error message, use SSO button'
              })}</Button.a>
            </div>)
          }
          {system.ssoEntities.map((sso, index) =>
            <div css="margin: 12px;" key={index}>
              <Button.a highlight href={sso.url}>{sso.label || intl.formatMessage({
                defaultMessage:'Use Single Sign On',
                description:'Login error message, use SSO button'
              })}</Button.a>
            </div>)
          }
        </>
        }
      </SignInCard>
      }

      {logout.isOpen && <SignInCard accountSettings={accountSettings.isOpen}>
        <h2><FormattedMessage
          defaultMessage="You have been logged out"
          description="Logout message"
        /></h2>
        {/* <button href="/sso">Login</button> */}
        <Button.a highlight href={`/sso?systemId=${system.systemId}&locale=${intl.locale}`}><FormattedMessage
          defaultMessage="Sign in Again"
          description="Logout message, sign in again button"
        /></Button.a>

      </SignInCard>
      }

      {!loginFailed.isOpen && !logout.isOpen && <SignInCard accountSettings={accountSettings.isOpen}>
        {/* Only one sso provider */}
        {system.ssoEntities.length === 1 &&
          <>
            <CardHeader>
              <h1><FormattedMessage
                defaultMessage="Loading {systemName}"
                description="Loading system message"
                values={{systemName: system.systemName}}
              /></h1>
              <p><FormattedMessage
                defaultMessage="You will be redirected to {systemName} authentication page"
                description="Loading system instructions"
                values={{systemName: system.ssoEntities[0].label || system.systemName}}
              /></p>
            </CardHeader>
            <RedirectLoading>
              <Loading size={52} borderWidth={5} absolute/>
              <div>{timeToRedirect.isOpen}</div>
            </RedirectLoading>

            {system.ssoEntities.map((sso, index) => <Button.a key={index} highlight href={sso.url} css="min-width:12rem;"><FormattedMessage
              defaultMessage="Take me now"
              description="Loading system, take me now button"
            /></Button.a>)}
          </>
        }

        {/* More than one sso provider */}
        {system.ssoEntities.length > 1 && <>
          <CardHeader>
            <h1>{system.systemName}</h1>
            <h3><FormattedMessage
              defaultMessage="Select your {systemName} authentication page"
              description="Select SSO provider message"
              values={{systemName: system.systemName}}
            /></h3>
          </CardHeader>
          {system.ssoEntities.map((sso, index) =>
            <div css="margin: 12px;" key={index}>
              <Button.a highlight href={sso.url}>{sso.label || intl.formatMessage({
                defaultMessage:'Use Single Sign On',
                description:'Select SSO provider button'
              })}</Button.a>
            </div>)
          }
        </>
        }
      </SignInCard>}
    </SignInLayout>
  )
}